import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FormField, Text, useResponsive } from '@common-fe/common-fe';

import { EMPTY_FIELD_VALUE } from '@/common/constants';
import AppButton from '@/components/controls/AppButton';
import { useGetTransactor } from '@/modules/transaction/hooks/useGetTransactor.query';

interface ProviderViewFieldProps {
  label: string;
  testId: string;
  children: React.ReactNode;
}
const ProviderViewField: React.FC<ProviderViewFieldProps> = ({ label, testId, children }) => {
  const { isMobile } = useResponsive();
  if (isMobile) {
    return (
      <Box direction="column" align="start">
        <Box width="medium">
          <Text color="textSecondary" weight="normal">
            {label}
          </Text>
        </Box>
        <Box margin={{ top: 'spacing2' }}>
          <FormField>
            <Text size="large" color="textBody" data-testid={testId} weight={700}>
              {children}
            </Text>
          </FormField>
        </Box>
      </Box>
    );
  }
  return (
    <Box direction="row" align="start">
      <Box width="medium">
        <Text color="textBody" weight="normal">
          {label}
        </Text>
      </Box>
      <Box width="medium">
        <Text color="textBody" data-testid={testId} weight="bold">
          {children}
        </Text>
      </Box>
    </Box>
  );
};
export interface Props {
  transactorId?: string;
  onCancel(): void;
}

const ProviderView: React.FC<Props> = ({ transactorId, onCancel }) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { formattedData: transactor } = useGetTransactor(transactorId);
  return (
    <>
      <Box
        background="module"
        pad="medium"
        overflow="hidden"
        round="moduleRound"
        data-testid="ProviderModal-view-wrapper"
      >
        <Box
          background="canvas"
          pad="medium"
          overflow="auto"
          round="container1Round"
          gap="spacing12"
          style={{ display: 'block' }}
        >
          <ProviderViewField label={t('Provider name')} testId="ProviderModal-providerName-label">
            {transactor?.providerName || EMPTY_FIELD_VALUE}
          </ProviderViewField>

          <ProviderViewField label={t('Provider address 1')} testId="ProviderModal-providerAddress1-label">
            {transactor?.providerAddress1 || EMPTY_FIELD_VALUE}
          </ProviderViewField>

          <ProviderViewField label={t('Provider address 2')} testId="ProviderModal-providerAddress2-label">
            {transactor?.providerAddress2 || EMPTY_FIELD_VALUE}
          </ProviderViewField>

          <ProviderViewField label={t('Provider city')} testId="ProviderModal-providerCity-label">
            {transactor?.providerCity || EMPTY_FIELD_VALUE}
          </ProviderViewField>

          <ProviderViewField label={t('Provider state')} testId="ProviderModal-providerState-label">
            {transactor?.providerState || EMPTY_FIELD_VALUE}
          </ProviderViewField>
          <ProviderViewField label={t('Provider zip code')} testId="ProviderModal-providerZipCode-label">
            {transactor?.providerZipCode || EMPTY_FIELD_VALUE}
          </ProviderViewField>
        </Box>
      </Box>
      <Box direction="row" justify="end" align="center" pad={{ top: 'spacing16' }}>
        <AppButton
          buttonType="secondary"
          width={isMobile ? '100%' : '140px'}
          type="button"
          onClick={onCancel}
        >
          {t('Close')}
        </AppButton>
      </Box>
    </>
  );
};
export default ProviderView;
